import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import jsQR from "jsqr";
import { api } from "../api";

// Scanner biglietti: fotocamera SOLO dopo azione esplicita, solo video (mai audio),
// nessun frame inviato/registrato — al backend va solo il token decodificato.
// Causa radice del "riquadro nero" precedente: srcObject veniva assegnato prima che il
// <video> esistesse nel DOM. Ora lo stream si attacca in un effetto dedicato post-mount,
// con attesa di loadedmetadata e play() gestito. Decoder: BarcodeDetector dove supportato,
// fallback jsQR locale. Tracce fermate a stop/chiusura/successo/unmount/background.
export function TicketScanner({ event, onClose, staffToken, onResult }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const canvasRef = useRef(null);
  const detectorRef = useRef(null);
  const devicesRef = useRef([]);
  const devIdxRef = useRef(-1);
  const [camState, setCamState] = useState("off"); // off | starting | on
  const [camErr, setCamErr] = useState(null);      // permission_denied|no_camera|not_supported|insecure|busy|play_failed
  const [camCount, setCamCount] = useState(0);
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {kind, title, at}
  const readDoneRef = useRef(false); // guardia anti doppia lettura (nessuna closure obsoleta)

  const stopCam = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach(tr => tr.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCamState("off");
  }, []);
  const close = useCallback(() => { stopCam(); onClose(); }, [stopCam, onClose]);

  useEffect(() => () => stopCam(), [stopCam]);
  useEffect(() => {
    const h = () => { if (document.hidden) stopCam(); };
    document.addEventListener("visibilitychange", h);
    return () => document.removeEventListener("visibilitychange", h);
  }, [stopCam]);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [close]);

  const verify = async (raw) => {
    const token = (raw || "").trim();
    if (!token || busy) return;
    setBusy(true);
    try {
      const { data } = staffToken
        ? await api.post("/event-staff/verify", { token: staffToken, ticket_token: token })
        : await api.post("/tickets/verify", { token, event_id: event.id });
      if (data.result === "ok") setResult({ kind: "ok", title: data.event_title });
      else if (data.result === "already_used") setResult({ kind: "already_used", at: data.checked_in_at });
      else if (data.result === "wrong_event") setResult({ kind: "wrong_event", title: data.event_title });
      else setResult({ kind: "cancelled" });
      onResult?.(data.result);
    } catch (e) {
      const s = e.response?.status;
      const d = e.response?.data?.detail;
      if (!e.response) setResult({ kind: "network" });
      else if (s === 404) setResult({ kind: "unknown" });
      else if (s === 403 && d === "wrong_org") setResult({ kind: "wrong_org" });
      else setResult({ kind: "error" });
    } finally { setBusy(false); }
  };

  // Lettura valida: stop immediato, nessuna lettura duplicata (guardia ref + server idempotente)
  const onCode = (data) => {
    if (readDoneRef.current) return;
    readDoneRef.current = true;
    if (!data) { readDoneRef.current = false; setResult({ kind: "unreadable" }); return; }
    stopCam();
    verify(data);
  };

  const decodeLoopJsQR = () => {
    const v = videoRef.current;
    if (!v || !streamRef.current) return;
    if (v.readyState === v.HAVE_ENOUGH_DATA && v.videoWidth) {
      const cv = canvasRef.current || (canvasRef.current = document.createElement("canvas"));
      cv.width = v.videoWidth; cv.height = v.videoHeight;
      const ctx = cv.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(v, 0, 0);
      try {
        const img = ctx.getImageData(0, 0, cv.width, cv.height);
        const code = jsQR(img.data, cv.width, cv.height);
        if (code) { onCode(code.data); return; }
      } catch { /* frame non pronto */ }
    }
    rafRef.current = requestAnimationFrame(decodeLoopJsQR);
  };
  const decodeLoopBD = async () => {
    const v = videoRef.current;
    if (!v || !streamRef.current || !detectorRef.current) return;
    try {
      const codes = await detectorRef.current.detect(v);
      if (codes?.length) { onCode(codes[0].rawValue); return; }
    } catch { /* frame non pronto */ }
    rafRef.current = requestAnimationFrame(decodeLoopBD);
  };
  const startDecode = async () => {
    if (!detectorRef.current && "BarcodeDetector" in window) {
      try {
        const fmts = await window.BarcodeDetector.getSupportedFormats();
        if (fmts.includes("qr_code")) detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
      } catch { /* fallback jsQR */ }
    }
    rafRef.current = requestAnimationFrame(detectorRef.current ? decodeLoopBD : decodeLoopJsQR);
  };

  // Attacco dello stream DOPO il mount del <video>: attende loadedmetadata e avvia play()
  useEffect(() => {
    if (camState !== "on" || !streamRef.current || !videoRef.current) return;
    const v = videoRef.current;
    let cancelled = false;
    (async () => {
      try {
        v.srcObject = streamRef.current;
        if (v.readyState < 1) await new Promise((res, rej) => { v.onloadedmetadata = res; v.onerror = () => rej(new Error("video")); });
        await v.play();
        if (!cancelled) startDecode();
      } catch { if (!cancelled) { setCamErr("play_failed"); stopCam(); } }
    })();
    return () => { cancelled = true; };
  }, [camState]); // eslint-disable-line react-hooks/exhaustive-deps

  const classifyError = (e) => {
    switch (e?.name) {
      case "NotAllowedError": case "SecurityError": return "permission_denied";
      case "NotFoundError": case "OverconstrainedError": return "no_camera";
      case "NotReadableError": case "AbortError": return "busy";
      default: return "play_failed";
    }
  };
  const startCam = async (deviceId) => {
    setResult(null); setCamErr(null); setCamState("starting");
    readDoneRef.current = false;
    if (!window.isSecureContext) { setCamErr("insecure"); setCamState("off"); return; }
    if (!navigator.mediaDevices?.getUserMedia) { setCamErr("not_supported"); setCamState("off"); return; }
    try {
      const video = deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: "environment" } };
      const s = await navigator.mediaDevices.getUserMedia({ video, audio: false });
      streamRef.current = s;
      setCamState("on");
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        devicesRef.current = devs.filter(d => d.kind === "videoinput");
        if (deviceId) devIdxRef.current = devicesRef.current.findIndex(d => d.deviceId === deviceId);
        setCamCount(devicesRef.current.length);
      } catch { /* enumeration facoltativa */ }
    } catch (e) { setCamErr(classifyError(e)); setCamState("off"); }
  };
  const switchCam = () => {
    const devs = devicesRef.current;
    if (devs.length < 2) return;
    devIdxRef.current = (devIdxRef.current + 1) % devs.length;
    const id = devs[devIdxRef.current].deviceId;
    stopCam();
    startCam(id);
  };

  const next = () => { setResult(null); setManual(""); readDoneRef.current = false; };

  const styles = {
    ok: "bg-green-50 border-green-300 text-green-700",
    already_used: "bg-amber-50 border-amber-300 text-amber-700",
    cancelled: "bg-red-50 border-red-300 text-red-700",
    unknown: "bg-red-50 border-red-300 text-red-700",
    wrong_event: "bg-red-50 border-red-300 text-red-700",
    wrong_org: "bg-red-50 border-red-300 text-red-700",
    unreadable: "bg-amber-50 border-amber-300 text-amber-700",
    network: "bg-red-50 border-red-300 text-red-700",
    error: "bg-red-50 border-red-300 text-red-700",
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" onClick={close} data-testid="scanner-modal">
      <div className="yp-card p-5 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t("scan.title")}>
        <div className="flex justify-between items-start gap-3 mb-1">
          <div>
            <h3 className="font-bold text-lg">{t("scan.title")}</h3>
            <p className="text-sm text-slate-500">{event.title}</p>
          </div>
          <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs shrink-0" onClick={close} aria-label={t("common.cancel")} data-testid="scanner-close">{t("common.cancel")}</button>
        </div>

        <div aria-live="polite">
        {result ? (
          <div className={`mt-3 border rounded-lg p-4 text-sm font-medium ${styles[result.kind]}`} role="status" data-testid="scan-result" data-result={result.kind}>
            {t(`scan.res_${result.kind}`)}
            {result.kind === "ok" && result.title && <div className="text-xs font-normal mt-1">{result.title}</div>}
            {result.kind === "already_used" && result.at && <div className="text-xs font-normal mt-1"><span className="sr-only">, </span>{new Date(result.at).toLocaleString()}</div>}
          </div>
        ) : (
          <>
            {camState === "off" && (
              <button className="yp-btn yp-btn-primary w-full mt-3" onClick={() => startCam()} disabled={busy} data-testid="scanner-start-camera">{t("scan.start_camera")}</button>)}
            {camState === "starting" && (
              <div className="mt-3 text-sm text-slate-500 flex items-center justify-center py-8" role="status" data-testid="scanner-starting">{t("scan.starting")}</div>)}
            {camState === "on" && (
              <div className="mt-3">
                <video ref={videoRef} autoPlay playsInline muted className="w-full rounded bg-black aspect-[4/3] object-cover" data-testid="scanner-video" />
                <div className="flex gap-2 mt-2">
                  <button className="yp-btn yp-btn-outline flex-1" onClick={stopCam} data-testid="scanner-stop-camera">{t("scan.stop_camera")}</button>
                  {camCount > 1 && (
                    <button className="yp-btn yp-btn-outline flex-1" onClick={switchCam} data-testid="scanner-switch-camera">{t("scan.switch_camera")}</button>)}
                </div>
              </div>)}
            {camErr && (
              <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3" role="alert" data-testid="scanner-camera-error" data-error={camErr}>
                {t(`scan.err_${camErr}`)}
              </div>)}
            <div className="mt-4">
              <label className="text-sm font-medium" htmlFor="manual-code">{t("scan.manual_label")}</label>
              <div className="flex gap-2 mt-1">
                <input id="manual-code" className="yp-input flex-1" value={manual} onChange={e => setManual(e.target.value)}
                  placeholder={t("scan.manual_ph")} data-testid="scanner-manual-input" autoComplete="off" />
                <button className="yp-btn yp-btn-primary shrink-0" onClick={() => verify(manual)} disabled={busy || !manual.trim()} data-testid="scanner-manual-verify">
                  {busy ? "…" : t("scan.verify")}
                </button>
              </div>
            </div>
          </>
        )}
        </div>

        {result && (
          <button className="yp-btn yp-btn-primary w-full mt-4" onClick={next} data-testid="scanner-next">{t("scan.next")}</button>
        )}
      </div>
    </div>
  );
}
